<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6">
      <!-- Header -->
      <div class="flex justify-end items-center mb-6">
        <div class="flex gap-2">
         <Link 
            v-if="$page.props.isAdminOrSuperAdmin"
            :href="route('modules.registri-personalizzati.builder', registry.id)"
            class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
          >
            <i class="fa fa-cog"></i>
            Configura Colonne
          </Link>
          <button
            v-if="hasMagoCustomerOrSupplier"
            @click="refreshMagoRefs"
            :disabled="refreshingMagoRefs"
            class="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
          >
            <i class="fa fa-refresh"></i>
            Aggiorna riferimenti mago
          </button>
          <button
            @click="exportExcel"
            class="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <i class="fa fa-file-excel"></i>
            Esporta Excel
          </button>
          <Link
            
            :href="route('modules.registri-personalizzati.import.index', registry.id)"
            class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <i class="fa fa-upload"></i>
            {{ $t('rp.import.button') }}
          </Link>
          <button 
            @click="openRecordModal()" 
            class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <i class="fa fa-plus"></i>
            Nuovo Record
          </button>
        </div>
      </div>

      <div v-if="customerFilter" class="mt-2 mb-3 flex items-center gap-2">
        <span class="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
          Filtro: {{ customerFilterLabel || customerFilter }}
        </span>
        <button 
          @click="clearCustomerFilter"
          class="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Cancella filtro
        </button>
      </div>

      <!-- PageServerTable for all views -->
      <PageServerTable
        ref="dataTable"
        :headers="tableHeaders"
        :items="tableItems"
        :items-per-page="pagination.per_page"
        :items-per-page-options="itemsPerPageOptions"
        :sort-by="tableSortBy"
        :items-length="pagination.total"
        :reload-fn="reloadTable"
        :item-value="'id'"
        :page="pagination.current_page"
        show-current-page
        sticky-header
        sticky-scrollbar
      >
        <!-- Actions column -->
        <template #item.actions="{ item }">
          <span class="flex gap-2">
            <button @click.stop="openRecordModal(item._original)" class="text-yellow-500 hover:text-yellow-700" title="Modifica">
              <v-icon name="bi-pencil" scale="1" />
            </button>
            <button @click.stop="confirmDeleteRecord(item._original)" class="text-red-500 hover:text-red-700" title="Elimina">
              <v-icon name="fa-trash" scale="1" />
            </button>
          </span>
        </template>

        <!-- Hyperlink columns -->
        <template v-for="col in hyperlinkColumns" :key="col.id" #[`item.col_${col.id}`]="{ item }">
          <a
            v-if="item['col_' + col.id] && item['col_' + col.id] !== '-'"
            :href="item['col_' + col.id]"
            target="_blank"
            rel="noopener noreferrer"
            class="text-blue-600 hover:text-blue-800 underline"
          >{{ item['col_' + col.id] }}</a>
          <span v-else>-</span>
        </template>
      </PageServerTable>

    </div>

    <!-- Record Modal -->
    <div 
      v-if="showRecordModal" 
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      style="padding-top: 80px;"
    >
      <div class="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" @click.stop>
        <h2 class="text-xl font-semibold mb-4">
          {{ editingRecord ? 'Modifica Record' : 'Nuovo Record' }}
        </h2>
        
        <form @submit.prevent="submitRecord">
          <div class="grid grid-cols-2 gap-4">
            <div 
              v-for="column in columns" 
              :key="'field-' + column.id"
              class="col-span-2 md:col-span-1"
            >
              <label class="block text-gray-700 text-sm font-medium mb-2">
                {{ column.name }}
                <span v-if="column.is_required" class="text-red-500">*</span>
              </label>
              
              <!-- Text Input -->
              <textarea
                v-if="column.type === 'text'"
                v-model="recordForm.values[column.id]"
                class="p93__input w-full resize-y min-h-[38px]"
                rows="2"
                :required="column.is_required"
              />

              <!-- Number Input -->
              <input 
                v-else-if="column.type === 'number'"
                v-model="recordForm.values[column.id]"
                type="number"
                class="p93__input w-full"
                :required="column.is_required"
              />

              <!-- Date Input -->
              <input 
                v-else-if="column.type === 'date'"
                v-model="recordForm.values[column.id]"
                type="date"
                class="p93__input w-full"
                :required="column.is_required"
              />

              <!-- Select -->
              <select 
                v-else-if="column.type === 'select'"
                v-model="recordForm.values[column.id]"
                class="p93__input w-full"
                :required="column.is_required"
              >
                <option value="">Seleziona...</option>
                <option 
                  v-for="option in getColumnOptions(column)" 
                  :key="option.value" 
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>

              <!-- Mago Query (select from SQL query) -->
              <VSelectWithPopper
                v-else-if="column.type === 'magoquery'"
                v-model="recordForm.values[column.id]"
                :options="getColumnOptions(column)"
                label="label"
                :reduce="opt => opt.value"
                placeholder="Seleziona..."
                :clearable="!column.is_required"
                class="w-full"
              />

              <!-- Portal Query (select from portal database SQL query) -->
              <VSelectWithPopper
                v-else-if="column.type === 'portalquery'"
                v-model="recordForm.values[column.id]"
                :options="getColumnOptions(column)"
                label="label"
                :reduce="opt => opt.value"
                placeholder="Seleziona..."
                :clearable="!column.is_required"
                class="w-full"
              />

              <!-- Mago Customer (popup picker) -->
              <div v-else-if="column.type === 'mago_customer'" class="relative w-full inline-flex items-center">
                <span
                  class="p93__input w-full cursor-pointer pr-10"
                  @click="openCustSuppPopup(column)"
                >
                  {{ getCustSuppFormLabel(column) || 'Cerca cliente...' }}
                </span>
                <span class="absolute right-3 flex items-center gap-1">
                  <v-icon
                    v-if="recordForm.values[column.id]"
                    name="fa-times"
                    scale="0.9"
                    class="cursor-pointer text-red-400 hover:text-red-600"
                    @click.stop="clearCustSuppField(column)"
                  />
                  <v-icon name="hi-arrows-expand" scale="1.1" class="text-gray-500 cursor-pointer" @click.stop="openCustSuppPopup(column)" />
                </span>
              </div>

              <!-- Mago Supplier (popup picker) -->
              <div v-else-if="column.type === 'mago_supplier'" class="relative w-full inline-flex items-center">
                <span
                  class="p93__input w-full cursor-pointer pr-10"
                  @click="openCustSuppPopup(column)"
                >
                  {{ getCustSuppFormLabel(column) || 'Cerca fornitore...' }}
                </span>
                <span class="absolute right-3 flex items-center gap-1">
                  <v-icon
                    v-if="recordForm.values[column.id]"
                    name="fa-times"
                    scale="0.9"
                    class="cursor-pointer text-red-400 hover:text-red-600"
                    @click.stop="clearCustSuppField(column)"
                  />
                  <v-icon name="hi-arrows-expand" scale="1.1" class="text-gray-500 cursor-pointer" @click.stop="openCustSuppPopup(column)" />
                </span>
              </div>

              <!-- Hyperlink Input -->
              <input
                v-else-if="column.type === 'hyperlink'"
                v-model="recordForm.values[column.id]"
                type="url"
                class="p93__input w-full"
                placeholder="https://"
                :required="column.is_required"
              />

              <!-- Mago CustSupp Ref (read-only, auto-populated) -->
              <input
                v-else-if="column.type === 'mago_custsupp_ref'"
                :value="recordForm.values[column.id] || ''"
                type="text"
                class="p93__input w-full bg-gray-50 text-gray-500"
                readonly
                title="Valore popolato automaticamente dal cliente/fornitore collegato"
              />

              <!-- Default to text -->
              <textarea
                v-else
                v-model="recordForm.values[column.id]"
                class="p93__input w-full resize-y min-h-[38px]"
                rows="2"
                :required="column.is_required"
              />
            </div>
          </div>

          <div class="flex justify-end gap-2 pt-6 mt-6 border-t">
            <button 
              type="button" 
              @click="closeRecordModal"
              class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
            >
              Annulla
            </button>
            <button 
              type="submit" 
              :disabled="recordForm.processing"
              class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              {{ editingRecord ? 'Aggiorna' : 'Salva' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- CustSupp Picker Popup -->
    <div
      v-if="showCustSuppPopup"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[60]"
      @click="closeCustSuppPopup"
    >
      <div class="bg-white rounded-lg w-11/12 max-h-[85vh] flex flex-col shadow-xl" @click.stop>
        <div class="px-4 py-3 border-b flex items-center justify-between bg-gray-50 rounded-t-lg">
          <h3 class="font-semibold text-gray-800">
            {{ custSuppPopupColumn?.type === 'mago_customer' ? 'Seleziona cliente' : 'Seleziona fornitore' }}
          </h3>
          <div class="flex items-center gap-4">
            <label class="flex items-center cursor-pointer">
              <input type="checkbox" v-model="custSuppShowDisabled" @change="loadCustSuppTable()" class="sr-only" />
              <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                :class="{ 'bg-green-500': custSuppShowDisabled }">
                <span
                  class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                  :class="{ 'translate-x-5': custSuppShowDisabled }" />
              </div>
              <span class="ml-3 text-gray-700 text-sm">Mostra disabilitati</span>
            </label>
            <button @click="closeCustSuppPopup" class="text-gray-400 hover:text-gray-600">
              <v-icon name="fa-times" scale="1.2" />
            </button>
          </div>
        </div>
        <div class="overflow-y-auto flex-1 p-4">
          <PageServerTable
            :headers="custSuppTableHeaders"
            :items="custSuppTableItems"
            :items-per-page="custSuppTableIpp"
            :items-per-page-options="custSuppTableIppo"
            :sort-by="custSuppTableSortby"
            :items-length="custSuppTableTotal"
            :reload-fn="loadCustSuppTable"
            :page="custSuppTablePage"
            show-current-page
            density="compact"
            hover
            @click:row="onCustSuppTableRowClick"
          />
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteModal" 
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      @click="closeDeleteModal"
    >
      <div class="bg-white p-6 rounded-lg w-full max-w-md" @click.stop>
        <h2 class="text-xl font-semibold mb-4 text-red-600">Conferma Eliminazione</h2>
        <p class="text-gray-700 mb-4">
          Sei sicuro di voler eliminare questo record? Questa azione non può essere annullata.
        </p>
        
        <div class="flex justify-end gap-2">
          <button 
            type="button" 
            @click="closeDeleteModal"
            class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
          >
            Annulla
          </button>
          <button 
            type="button" 
            @click="deleteRecord"
            class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
          >
            Elimina
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, useForm, router } from '@inertiajs/vue3';
import axios from 'axios';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import VSelectWithPopper from '@/Components/VSelectWithPopper.vue';
import 'vue-select/dist/vue-select.css';

export default {
  components: {
    Link,
    BreadCrumbs,
    PageServerTable,
    VSelectWithPopper
  },
  props: {
    registry: Object,
    columns: Array,
    rows: Array,
    pagination: Object,
    columnsOptions: Object,
    columnTypes: Array,
    customerFilter: {
      type: String,
      default: null
    },
    customerFilterLabel: {
      type: String,
      default: null
    }
  },
  data() {
    const urlParams = new URLSearchParams(window.location.search);
    
    return {
      showRecordModal: false,
      showDeleteModal: false,
      editingRecord: null,
      recordToDelete: null,
      refreshingMagoRefs: false,
      showCustSuppPopup: false,
      custSuppPopupColumn: null,
      custSuppFormLabels: {},
      custSuppTableHeaders: [],
      custSuppTableExtraHeaders: [],
      custSuppTableSortby: [{ key: 'CustSupp', order: 'asc' }],
      custSuppTableItems: [],
      custSuppTableTotal: 0,
      custSuppTablePage: 1,
      custSuppTableIpp: 10,
      custSuppTableIppo: [
        { value: 10, title: '10' },
        { value: 25, title: '25' },
        { value: 50, title: '50' },
      ],
      custSuppShowDisabled: false,
      sortColumn: urlParams.get('sort_column') || null,
      sortDirection: urlParams.get('sort_direction') || 'asc',
      recordForm: useForm({
        values: {}
      }),
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Registri Personalizzati', href: route('modules.registri-personalizzati.index') },
        { name: this.registry?.name || 'Registro', href: '#' }
      ],
      itemsPerPageOptions: [
        { value: 10, title: '10' },
        { value: 25, title: '25' },
        { value: 50, title: '50' },
        { value: 100, title: '100' },
      ],
      tableHeaders: []
    };
  },
  computed: {
    hasMagoCustomerOrSupplier() {
      return this.columns.some(col => col.type === 'mago_customer' || col.type === 'mago_supplier');
    },
    tableItems() {
      return this.rows.map(row => {
        const item = { id: row.id, _original: row };
        this.columns.forEach(col => {
          const rawValue = row.values[col.id]?.raw;
          if (col.type === 'mago_customer' || col.type === 'mago_supplier') {
            item['col_' + col.id] = this.getMagoCustSuppLabel(col, rawValue);
          } else if (this.isSelectTypeColumn(col)) {
            item['col_' + col.id] = this.getOptionLabel(col, rawValue);
          } else if (col.type === 'date') {
            item['col_' + col.id] = this.formatDate(rawValue);
          } else {
            item['col_' + col.id] = rawValue || '-';
          }
        });
        return item;
      });
    },
    tableSortBy() {
      if (this.sortColumn) {
        return [{ key: 'col_' + this.sortColumn, order: this.sortDirection }];
      }
      return [];
    },
    hyperlinkColumns() {
      return this.columns.filter(col => col.type === 'hyperlink');
    }
  },
  mounted() {
    this.buildTableHeaders();
  },
  methods: {
    buildTableHeaders() {
      const urlParams = new URLSearchParams(window.location.search);
      let initialFilters = {};
      const filtersParam = urlParams.get('filters');
      if (filtersParam) {
        try {
          initialFilters = JSON.parse(filtersParam);
        } catch (e) {
          initialFilters = {};
        }
      }
      
      const headers = this.columns
        .filter(col => !col.is_hidden)
        .map(col => {
          const header = {
            title: col.name,
            align: 'start',
            value: 'col_' + col.id,
            key: 'col_' + col.id,
            filterable: true,
            filtervalue: initialFilters[col.id] || '',
            columnId: col.id,
            ...(col.width ? { width: col.width, cellProps: { style: `word-break: break-word;${col.width.includes('%') ? '' : ` min-width: ${col.width};`}` } } : {})
          };
          
          if (this.isSelectTypeColumn(col)) {
            header.select = true;
            header.selectPlaceholder = 'Tutti';
            header.selectOptions = this.getColumnOptions(col).map(opt => ({
              value: opt.value,
              text: opt.label
            }));
          } else if (col.type === 'date') {
            header.date = true;
          }
          
          return header;
        });
      headers.push({ 
        title: 'Azioni',
        value: 'actions', 
        key: 'actions',
        sortable: false, 
        filterable: false 
      });
      this.tableHeaders = headers;
    },
    reloadTable(sortby = undefined, itemsperpage = undefined, page = undefined) {
      const params = {};
      
      if (this.customerFilter) {
        params.customer = this.customerFilter;
      }
      
      if (sortby && sortby.length > 0) {
        const sortKey = sortby[0].key;
        if (sortKey && sortKey.startsWith('col_')) {
          params.sort_column = sortKey.replace('col_', '');
          params.sort_direction = sortby[0].order || 'asc';
          this.sortColumn = params.sort_column;
          this.sortDirection = params.sort_direction;
        }
      } else if (sortby !== undefined) {
        this.sortColumn = null;
        this.sortDirection = 'asc';
      }
      
      if (itemsperpage) {
        params.per_page = itemsperpage;
      }
      
      if (page) {
        params.page = page;
      }
      
      const activeFilters = {};
      this.tableHeaders.forEach(header => {
        if (header.columnId && header.filtervalue && header.filtervalue.toString().trim()) {
          activeFilters[header.columnId] = header.filtervalue;
        }
      });
      
      if (Object.keys(activeFilters).length > 0) {
        params.filters = JSON.stringify(activeFilters);
      }
      
      router.get(route('modules.registri-personalizzati.show', this.registry.id), params, {
        preserveState: true,
        preserveScroll: true
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('it-IT');
      } catch (e) {
        return dateStr;
      }
    },
    isSelectTypeColumn(column) {
      return ['select', 'magoquery', 'portalquery', 'mago_customer', 'mago_supplier'].includes(column.type);
    },
    isCustSuppRefColumn(column) {
      return column.type === 'mago_custsupp_ref';
    },
    getColumnOptions(column) {
      const columnId = String(column.id);
      if (this.columnsOptions && this.columnsOptions[columnId]) {
        return this.columnsOptions[columnId];
      }
      if (this.columnsOptions && this.columnsOptions[column.id]) {
        return this.columnsOptions[column.id];
      }
      
      if (!column.value_type) return [];
      
      try {
        const parsed = JSON.parse(column.value_type);
        if (Array.isArray(parsed)) {
          return parsed.map(opt => ({
            value: typeof opt === 'object' ? opt.value : opt,
            label: typeof opt === 'object' ? opt.label : opt
          }));
        }
      } catch (e) {
      }
      
      return [];
    },
    getMagoCustSuppLabel(column, value) {
      if (!value) return '-';
      const options = this.getColumnOptions(column);
      const option = options.find(opt => opt.value === value || opt.value == value);
      if (!option) return value;
      return `${value} (${option.label})`;
    },
    getOptionLabel(column, value) {
      if (!value) return '-';
      const options = this.getColumnOptions(column);
      const option = options.find(opt => opt.value === value || opt.value == value);
      return option ? option.label : value;
    },
    openRecordModal(record = null) {
      this.editingRecord = record;
      this.custSuppFormLabels = {};
      
      const values = {};
      this.columns.forEach(column => {
        if (record && record.values[column.id]) {
          values[column.id] = record.values[column.id].raw || '';
        } else {
          values[column.id] = '';
        }

        if ((column.type === 'mago_customer' || column.type === 'mago_supplier') && values[column.id]) {
          const code = values[column.id];
          const opt = this.getColumnOptions(column).find(o => o.value === code || o.value == code);
          if (opt) {
            if (!this.custSuppFormLabels[column.id]) this.custSuppFormLabels[column.id] = {};
            this.custSuppFormLabels[column.id][code] = opt.label;
          }
        }
      });
      
      this.recordForm.values = values;
      this.showRecordModal = true;
    },
    closeRecordModal() {
      this.showRecordModal = false;
      this.editingRecord = null;
      this.recordForm.reset();
    },
    submitRecord() {
      const formData = {
        values: this.recordForm.values
      };

      if (this.editingRecord) {
        router.put(
          route('modules.registri-personalizzati.records.update', [this.registry.id, this.editingRecord.id]),
          formData,
          {
            onSuccess: () => {
              this.closeRecordModal();
            }
          }
        );
      } else {
        router.post(
          route('modules.registri-personalizzati.records.store', this.registry.id),
          formData,
          {
            onSuccess: () => {
              this.closeRecordModal();
            }
          }
        );
      }
    },
    clearCustomerFilter() {
      router.visit(route('modules.registri-personalizzati.show', this.registry.id));
    },
    exportExcel() {
      const params = new URLSearchParams();

      if (this.sortColumn) {
        params.set('sort_column', this.sortColumn);
        params.set('sort_direction', this.sortDirection);
      }

      const activeFilters = {};
      this.tableHeaders.forEach(header => {
        if (header.columnId && header.filtervalue && header.filtervalue.toString().trim()) {
          activeFilters[header.columnId] = header.filtervalue;
        }
      });
      if (Object.keys(activeFilters).length > 0) {
        params.set('filters', JSON.stringify(activeFilters));
      }

      if (this.customerFilter) {
        params.set('customer', this.customerFilter);
      }

      const url = route('modules.registri-personalizzati.export', this.registry.id) + (params.toString() ? '?' + params.toString() : '');
      window.location.href = url;
    },
    confirmDeleteRecord(record) {
      this.recordToDelete = record;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.recordToDelete = null;
    },
    deleteRecord() {
      router.delete(
        route('modules.registri-personalizzati.records.destroy', [this.registry.id, this.recordToDelete.id]),
        {
          onSuccess: () => {
            this.closeDeleteModal();
          }
        }
      );
    },
    getCustSuppFormLabel(column) {
      const code = this.recordForm.values[column.id];
      if (!code) return '';
      const label = this.custSuppFormLabels[column.id]?.[code];
      if (label) return `${code} (${label})`;
      return code;
    },
    clearCustSuppField(column) {
      this.recordForm.values[column.id] = '';
      this.columns.forEach(col => {
        if (col.type === 'mago_custsupp_ref' && col.value_type) {
          try {
            const config = JSON.parse(col.value_type);
            if (String(config.ref_column_id) === String(column.id)) {
              this.recordForm.values[col.id] = '';
            }
          } catch (e) {}
        }
      });
    },
    openCustSuppPopup(column) {
      this.custSuppPopupColumn = column;
      this.custSuppTableHeaders = [
        { title: 'Codice', key: 'CustSupp', value: 'CustSupp', filterable: true, filtervalue: '' },
        { title: 'Ragione Sociale', key: 'CompanyName', value: 'CompanyName', filterable: true, filtervalue: '' },
        { title: 'Indirizzo', key: 'Address', value: 'Address', filterable: true, filtervalue: '' },
        { title: 'CAP', key: 'ZIPCode', value: 'ZIPCode', filterable: true, filtervalue: '' },
        { title: 'Città', key: 'City', value: 'City', filterable: true, filtervalue: '' },
        { title: 'Provincia', key: 'County', value: 'County', filterable: true, filtervalue: '' },
      ];
      const visibleKeys = new Set(this.custSuppTableHeaders.map(h => h.key));
      this.custSuppTableExtraHeaders = [];
      this.columns.forEach(col => {
        if (col.type === 'mago_custsupp_ref' && col.value_type) {
          try {
            const config = JSON.parse(col.value_type);
            if (String(config.ref_column_id) === String(column.id) && config.ma_field && !visibleKeys.has(config.ma_field)) {
              this.custSuppTableExtraHeaders.push({ key: config.ma_field, _refColumnId: col.id });
              visibleKeys.add(config.ma_field);
            }
          } catch (e) {}
        }
      });
      this.custSuppTableSortby = [{ key: 'CustSupp', order: 'asc' }];
      this.custSuppTableItems = [];
      this.custSuppTableTotal = 0;
      this.custSuppTablePage = 1;
      this.custSuppShowDisabled = false;
      this.showCustSuppPopup = true;
      this.loadCustSuppTable();
    },
    closeCustSuppPopup() {
      this.showCustSuppPopup = false;
      this.custSuppPopupColumn = null;
    },
    loadCustSuppTable(sortby = undefined, itemsperpage = undefined, page = undefined) {
      if (!this.custSuppPopupColumn) return;

      if (sortby) this.custSuppTableSortby = sortby;
      if (itemsperpage) this.custSuppTableIpp = itemsperpage;
      if (page) this.custSuppTablePage = page;

      axios.post(route('modules.registri-personalizzati.api.search-custsupp'), {
        headers: [...this.custSuppTableHeaders, ...this.custSuppTableExtraHeaders],
        sortby: this.custSuppTableSortby,
        itemsperpage: this.custSuppTableIpp,
        page: this.custSuppTablePage,
        custsupptype: this.custSuppPopupColumn.type,
        show_disabled: this.custSuppShowDisabled,
      }).then(response => {
        this.custSuppTableItems = response.data.items;
        this.custSuppTablePage = response.data.page;
        this.custSuppTableTotal = response.data.total;
      }).catch(() => {
        this.custSuppTableItems = [];
      });
    },
    onCustSuppTableRowClick(event, row) {
      this.selectCustSupp(row.item);
    },
    selectCustSupp(item) {
      const colId = this.custSuppPopupColumn.id;
      this.recordForm.values[colId] = item.CustSupp;
      if (!this.custSuppFormLabels[colId]) {
        this.custSuppFormLabels[colId] = {};
      }
      this.custSuppFormLabels[colId][item.CustSupp] = item.CompanyName;
      this.columns.forEach(col => {
        if (col.type === 'mago_custsupp_ref' && col.value_type) {
          try {
            const config = JSON.parse(col.value_type);
            if (String(config.ref_column_id) === String(colId) && config.ma_field) {
              this.recordForm.values[col.id] = item[config.ma_field] ?? '';
            }
          } catch (e) {}
        }
      });
      this.closeCustSuppPopup();
    },
    resolveRefPreview() {
      const hasRefCols = this.columns.some(c => c.type === 'mago_custsupp_ref');
      if (!hasRefCols) return;
      axios.post(route('modules.registri-personalizzati.api.resolve-refs', this.registry.id), {
        values: this.recordForm.values
      }).then(response => {
        const resolved = response.data.values;
        Object.keys(resolved).forEach(colId => {
          if (resolved[colId] !== undefined) {
            this.recordForm.values[colId] = resolved[colId] ?? '';
          }
        });
      }).catch(() => {});
    },
    refreshMagoRefs() {
      this.refreshingMagoRefs = true;
      router.post(
        route('modules.registri-personalizzati.records.refresh-mago-refs', this.registry.id),
        {},
        {
          onFinish: () => {
            this.refreshingMagoRefs = false;
          }
        }
      );
    }
  }
};
</script>

